import { Platform } from 'react-native';
import { showSuccess } from '../../shared/ui/appToast';
import * as Clipboard from 'expo-clipboard';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { formatSaleCreatedAt } from '../../sales/application/salePresentation';
import {
  buildAfipQrUrl,
  buildFiscalComprobanteFilename,
  escapeFiscalHtml,
  formatComprobanteNumber,
  formatFiscalCuit,
  formatFiscalMoney,
  type FiscalComprobanteExportInput,
  type FiscalComprobanteExportItem,
} from './fiscalComprobanteFormat';
import { spanishInvoiceTypeLabel } from '../domain/invoiceTypes';
import { formatQty } from '@shared/quantity';

export type { FiscalComprobanteExportInput, FiscalComprobanteExportItem };

function buildPlainTextSummary(input: FiscalComprobanteExportInput, qrUrl: string | null): string {
  const lines: string[] = [];
  const tipo = spanishInvoiceTypeLabel(input.invoiceType);
  const nro = formatComprobanteNumber(input.pointOfSale, input.invoiceNumber);
  if (input.emitterBusinessName) lines.push(input.emitterBusinessName);
  if (input.emitterCuit) lines.push(`CUIT emisor: ${formatFiscalCuit(input.emitterCuit) ?? input.emitterCuit}`);
  lines.push(`${tipo}${nro ? ` Nº ${nro}` : ''}`);
  lines.push(`Fecha: ${formatSaleCreatedAt(input.issuedAt ?? input.saleCreatedAt)}`);
  lines.push(`Total: ${formatFiscalMoney(input.saleTotal)}`);
  lines.push(`CAE: ${input.cae}`);
  if (input.caeExpiration) {
    lines.push(`Vto. CAE: ${formatSaleCreatedAt(input.caeExpiration)}`);
  }
  if (input.buyerName) lines.push(`Cliente: ${input.buyerName}`);
  if (input.buyerCuit) lines.push(`CUIT cliente: ${formatFiscalCuit(input.buyerCuit) ?? input.buyerCuit}`);
  if (qrUrl) lines.push(`Verificar ARCA: ${qrUrl}`);
  lines.push(`Ref. venta: ${input.saleRef}`);
  return lines.join('\n');
}

async function tryQrImageMarkup(value: string): Promise<string | null> {
  try {
    const QRCode = await import('qrcode');
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const dataUrl = await QRCode.toDataURL(value, {
        width: 160,
        margin: 1,
        color: { dark: '#111827', light: '#ffffff' },
      });
      return `<img src="${dataUrl}" alt="QR ARCA" width="160" height="160" />`;
    }
    const svg = await QRCode.toString(value, {
      type: 'svg',
      width: 160,
      margin: 1,
      color: { dark: '#111827', light: '#ffffff' },
    });
    return svg.replace('<svg ', '<svg style="width:160px;height:160px;" ');
  } catch {
    return null;
  }
}

export async function buildFiscalComprobanteHtml(
  input: FiscalComprobanteExportInput,
  options?: { forPdf?: boolean }
): Promise<string> {
  const forPdf = options?.forPdf ?? false;
  const qrUrl = buildAfipQrUrl(input.qrPayload, input.qrUrl, {
    buyerCuit: input.buyerCuit,
    cae: input.cae,
  });
  const qrMarkup = qrUrl ? await tryQrImageMarkup(qrUrl) : null;
  const tipo = spanishInvoiceTypeLabel(input.invoiceType);
  const nro = formatComprobanteNumber(input.pointOfSale, input.invoiceNumber);
  const emitterCuit = formatFiscalCuit(input.emitterCuit);
  const buyerCuit = formatFiscalCuit(input.buyerCuit);
  const issuedLabel = formatSaleCreatedAt(input.issuedAt ?? input.saleCreatedAt);
  const caeVto = input.caeExpiration ? formatSaleCreatedAt(input.caeExpiration) : null;

  const itemRows = input.items
    .map((it) => {
      const lineTotal = it.price * it.quantity;
      const qty = it.quantity !== 1 ? ` × ${formatQty(it.quantity, it.unitOfMeasure)}` : '';
      return `<tr class="item-row">
        <td class="item-label">${escapeFiscalHtml(it.label)}${qty}${it.sku ? `<div class="muted">SKU ${escapeFiscalHtml(it.sku)}</div>` : ''}</td>
        <td class="num">${formatFiscalMoney(lineTotal)}</td>
      </tr>`;
    })
    .join('');

  const pageStyles = forPdf
    ? `
    @page { size: A4 portrait; margin: 14mm 12mm; }
    html, body { width: 100%; }
    body { background: #fff; padding: 0; font-size: 11px; }
    .sheet { border: none; border-radius: 0; max-width: none; padding: 0; box-shadow: none; }
    table { page-break-inside: auto; }
    thead { display: table-header-group; }
    tr.item-row { page-break-inside: avoid; break-inside: avoid; }
    .footer-block { page-break-inside: avoid; break-inside: avoid; }
    .qr-wrap { page-break-inside: avoid; break-inside: avoid; }
    `
    : `
    body { background: #f3f4f6; padding: 24px; }
    .sheet { max-width: 720px; margin: 0 auto; border-radius: 16px; padding: 28px; }
    `;

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeFiscalHtml(tipo)} · Ref. ${escapeFiscalHtml(input.saleRef)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: "Segoe UI", system-ui, sans-serif; color: #111827; margin: 0; overflow-x: hidden; }
    .sheet { background: #fff; border: 1px solid #e5e7eb; overflow: hidden; }
    h1 { font-size: 22px; margin: 0 0 4px; }
    .kicker { color: #6b7280; font-size: 12px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 700; margin-bottom: 8px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 20px 0; }
    .cell { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #f9fafb; }
    .label { color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 700; margin-bottom: 4px; }
    .value { font-size: 15px; font-weight: 700; }
    .cae { font-size: 18px; letter-spacing: 0.04em; }
    table { width: 100%; border-collapse: collapse; margin-top: 16px; }
    th, td { padding: 10px 0; border-bottom: 1px solid #e5e7eb; vertical-align: top; text-align: left; }
    th { color: #6b7280; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    td.item-label { word-break: break-word; white-space: normal; max-width: 1px; }
    .muted { color: #6b7280; font-size: 12px; margin-top: 2px; }
    .total { display: flex; justify-content: space-between; align-items: baseline; margin-top: 16px; padding-top: 12px; border-top: 2px solid #111827; page-break-inside: avoid; }
    .total-label { font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
    .total-value { font-size: 24px; font-weight: 900; }
    .qr-wrap { margin-top: 20px; display: flex; gap: 20px; align-items: flex-start; flex-wrap: wrap; max-width: 100%; }
    .qr-wrap img, .qr-wrap svg { width: 160px; height: 160px; flex-shrink: 0; border: 1px solid #e5e7eb; border-radius: 12px; }
    .qr-body { flex: 1; min-width: 0; max-width: 100%; }
    .qr-link { display: inline-block; max-width: 100%; color: #2563eb; font-weight: 600; text-decoration: none; overflow-wrap: anywhere; word-break: break-word; }
    .footer { margin-top: 24px; color: #6b7280; font-size: 12px; line-height: 1.5; }
    ${pageStyles}
  </style>
</head>
<body>
  <div class="sheet">
    <div class="kicker">Comprobante electrónico ARCA</div>
    <h1>${escapeFiscalHtml(input.emitterBusinessName ?? 'Comprobante fiscal')}</h1>
    ${emitterCuit ? `<div class="muted">CUIT ${escapeFiscalHtml(emitterCuit)}</div>` : ''}
    <div class="grid">
      <div class="cell"><div class="label">Tipo</div><div class="value">${escapeFiscalHtml(tipo)}</div></div>
      <div class="cell"><div class="label">Número</div><div class="value">${escapeFiscalHtml(nro ?? '—')}</div></div>
      <div class="cell"><div class="label">Fecha emisión</div><div class="value">${escapeFiscalHtml(issuedLabel)}</div></div>
      <div class="cell"><div class="label">Ref. venta</div><div class="value">${escapeFiscalHtml(input.saleRef)}</div></div>
    </div>
    ${input.buyerName || buyerCuit ? `<div class="grid">
      ${input.buyerName ? `<div class="cell"><div class="label">Cliente</div><div class="value">${escapeFiscalHtml(input.buyerName)}</div></div>` : ''}
      ${buyerCuit ? `<div class="cell"><div class="label">CUIT cliente</div><div class="value">${escapeFiscalHtml(buyerCuit)}</div></div>` : ''}
    </div>` : ''}
    <table class="items-table">
      <thead><tr><th>Detalle</th><th class="num">Importe</th></tr></thead>
      <tbody>${itemRows || '<tr><td colspan="2">Sin ítems</td></tr>'}</tbody>
    </table>
    <div class="total"><div class="total-label">Total</div><div class="total-value">${formatFiscalMoney(input.saleTotal)}</div></div>
    <div class="footer-block">
      <div class="grid">
        <div class="cell"><div class="label">CAE</div><div class="value cae">${escapeFiscalHtml(input.cae)}</div></div>
        ${caeVto ? `<div class="cell"><div class="label">Vto. CAE</div><div class="value">${escapeFiscalHtml(caeVto)}</div></div>` : ''}
      </div>
      ${qrMarkup || qrUrl ? `<div class="qr-wrap">
        ${qrMarkup ?? ''}
        <div class="qr-body">
          <div class="label">Verificación ARCA</div>
          ${qrUrl
            ? `<a class="qr-link" href="${escapeFiscalHtml(qrUrl)}" target="_blank" rel="noopener noreferrer">Verificar comprobante en ARCA</a>`
            : '<div class="muted">QR no disponible</div>'}
        </div>
      </div>` : ''}
      <div class="footer">Documento generado por Vuelto. Comprobante autorizado por ARCA. Conservá este archivo como respaldo de la operación.</div>
    </div>
  </div>
</body>
</html>`;
}

function downloadBlobWeb(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function shareNativePdf(uri: string, dialogTitle: string): Promise<void> {
  const canShare = await Sharing.isAvailableAsync();
  if (!canShare) {
    throw new Error('No hay una app disponible para guardar o compartir el PDF en este dispositivo.');
  }
  await Sharing.shareAsync(uri, {
    mimeType: 'application/pdf',
    dialogTitle,
    UTI: 'com.adobe.pdf',
  });
}

export async function downloadFiscalComprobantePdf(input: FiscalComprobanteExportInput): Promise<void> {
  const filename = buildFiscalComprobanteFilename(input, 'pdf');

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    const { buildFiscalComprobantePdfBlob } = await import('./fiscalInvoicePdfWeb');
    const blob = await buildFiscalComprobantePdfBlob(input);
    downloadBlobWeb(filename, blob);
    return;
  }

  const html = await buildFiscalComprobanteHtml(input, { forPdf: true });
  const { uri } = await Print.printToFileAsync({
    html,
    base64: false,
    width: 595,
    height: 842,
  });
  await shareNativePdf(uri, 'Guardar o compartir comprobante PDF');
}

export async function copyFiscalComprobanteSummary(input: FiscalComprobanteExportInput): Promise<void> {
  const qrUrl = buildAfipQrUrl(input.qrPayload, input.qrUrl, {
    buyerCuit: input.buyerCuit,
    cae: input.cae,
  });
  await Clipboard.setStringAsync(buildPlainTextSummary(input, qrUrl));
  showSuccess('Copiado', 'Datos del comprobante copiados al portapapeles.');
}

